import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  BookOpen, 
  FileText, 
  Image as ImageIcon,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  CloudUpload,
  X,
  Info,
  AlertTriangle,
  Maximize2, 
  Crop,
  AlertOctagon,
  Ruler,
  Lightbulb,
  XCircle
} from 'lucide-react';
import { booksService, digitalOceanService } from '../services/api';

const AdminBookUpload = () => {
  const navigate = useNavigate();
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    cover: { progress: 0, status: 'idle', fileName: '', error: null },
    pdf: { progress: 0, status: 'idle', fileName: '', error: null }
  });
  
  const [uploadedFiles, setUploadedFiles] = useState({
    coverUrl: '',
    pdfUrl: '',
    coverPreview: '',
    coverDimensions: { width: 0, height: 0, aspectRatio: 0 }
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author_bio: '',
    genre: '',
    age_rating: 'all',
    price: '',
    author_name: '',
    author_email: '',
    status: 'approved',
    sample_pages_count: 5
  });

  const [imageValidation, setImageValidation] = useState({
    isValid: false,
    message: '',
    warnings: []
  });

  const coverFileRef = useRef(null);
  const imagePreviewRef = useRef(null);

  // Define constants for dimensions
  const MIN_WIDTH = 328;
  const MIN_HEIGHT = 500;
  const TARGET_RATIO = 0.656; // 328/500 = 0.656 (3:4.57 ratio)
  const MAX_SIZE_MULTIPLIER = 3; // Max 3x the minimum size

  const ageRatings = [
    { value: 'all', label: 'All Ages (0+)' },
    { value: 'teen', label: 'Teen (13-17)' },
    { value: 'adult', label: 'Adult (18+)' },
  ];

  // Fetch genres
  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await booksService.getGenres();
        setGenres(response || []);
      } catch (error) {
        console.error('Error fetching genres:', error);
        setGenres([
          { id: 1, name: 'Fiction' },
          { id: 2, name: 'Non-Fiction' },
          { id: 3, name: 'Science Fiction' },
          { id: 4, name: 'Fantasy' },
          { id: 5, name: 'Mystery' },
          { id: 6, name: 'Romance' },
          { id: 7, name: 'Thriller' },
          { id: 8, name: 'Horror' },
          { id: 9, name: 'Biography' },
          { id: 10, name: 'Self-Help' },
        ]);
      }
    };

    fetchGenres();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Image validation function - Updated with 328x500 requirements
  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      // File type validation
      if (!file.type.startsWith('image/')) {
        reject(new Error('❌ Please upload an image file (JPG, PNG, WebP)'));
        return;
      }

      // Size validation
      if (file.size > 5 * 1024 * 1024) {
        reject(new Error('❌ Cover image must be less than 5MB'));
        return;
      }

      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          const aspectRatio = width / height;
          const ratioDiff = Math.abs(aspectRatio - TARGET_RATIO) / TARGET_RATIO;
          
          // STRICT VALIDATION - REJECT IF NOT MET
          const errors = [];
          const warnings = [];
          
          // Minimum size check - REJECT if too small
          if (width < MIN_WIDTH || height < MIN_HEIGHT) {
            errors.push(`Image too small: ${width}×${height}px`);
            errors.push(`Minimum required: ${MIN_WIDTH}×${MIN_HEIGHT}px`);
          }
          
          // Maximum size check - REJECT if too large (more than 3x minimum)
          const maxWidth = MIN_WIDTH * MAX_SIZE_MULTIPLIER;
          const maxHeight = MIN_HEIGHT * MAX_SIZE_MULTIPLIER;
          if (width > maxWidth || height > maxHeight) {
            errors.push(`Image too large: ${width}×${height}px`);
            errors.push(`Maximum allowed: ${maxWidth}×${maxHeight}px (${MAX_SIZE_MULTIPLIER}x minimum)`);
          }
          
          // Aspect ratio tolerance - REJECT if too far off (15% tolerance)
          if (ratioDiff > 0.15) {
            errors.push(`Aspect ratio ${width}:${height} (${aspectRatio.toFixed(2)})`);
            errors.push(`Required ratio: close to ${MIN_WIDTH}:${MIN_HEIGHT} (${TARGET_RATIO.toFixed(3)})`);
          }
          
          // For optimal quality - warnings only
          const optimalWidth = 600;
          const optimalHeight = 914; // 600/914 ≈ 0.656
          if (width < optimalWidth || height < optimalHeight) {
            warnings.push(`For best quality: ${optimalWidth}×${optimalHeight}px or similar`);
          }
          
          if (errors.length > 0) {
            reject(new Error(
              `❌ Cover image requirements not met:\n\n${errors.join('\n')}\n\n` +
              `Please use an image close to ${MIN_WIDTH}×${MIN_HEIGHT}px (ratio: ${TARGET_RATIO.toFixed(3)})`
            ));
            return;
          }
          
          // Calculate how close to ideal
          const widthDiffPercent = ((width - MIN_WIDTH) / MIN_WIDTH) * 100;
          const heightDiffPercent = ((height - MIN_HEIGHT) / MIN_HEIGHT) * 100;
          
          let message;
          if (width <= optimalWidth && height <= optimalHeight) {
            message = '✅ Image meets minimum requirements';
          } else if (width <= optimalWidth * 1.5 && height <= optimalHeight * 1.5) {
            message = '✅ Good image size and ratio';
          } else {
            message = '✅ Optimal image size and ratio';
          }
          
          const validationResult = {
            isValid: true,
            message,
            warnings,
            dimensions: { width, height, aspectRatio }
          };
          
          resolve(validationResult);
        };
        
        img.onerror = () => reject(new Error('❌ Failed to load image'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('❌ Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle cover upload
  const handleCoverUpload = async (file) => {
    if (!file) return;
  
    try {
      // Validate image STRICTLY before upload
      const validation = await validateImage(file);
      setImageValidation(validation);
      
      // If we get here, validation passed
      setUploadProgress(prev => ({
        ...prev,
        cover: { progress: 0, status: 'uploading', fileName: file.name, error: null }
      }));
      
      // Show preview
      const previewUrl = URL.createObjectURL(file);
      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: previewUrl,
        coverDimensions: validation.dimensions
      }));
        
        // Get upload URL
        const uploadData = await digitalOceanService.generateBookCoverUploadURL({
          filename: file.name,
          metadata: {
            width: validation.dimensions.width,
            height: validation.dimensions.height,
            aspect_ratio: validation.dimensions.aspectRatio.toFixed(4)
          }
        });
        
        if (!uploadData.success) {
          throw new Error(uploadData.error || 'Failed to get upload URL');
        }
        
        // Upload to DigitalOcean
        await digitalOceanService.uploadToSpaces(
          uploadData.upload_url,
          uploadData.fields,
          file
        );
        
        // Update state
        setUploadProgress(prev => ({
          ...prev,
          cover: { ...prev.cover, status: 'success', progress: 100 }
        }));
        
        setUploadedFiles(prev => ({
          ...prev,
          coverUrl: uploadData.public_url
        }));
        
      } catch (error) {
      // Show error message clearly
      setUploadProgress(prev => ({
        ...prev,
        cover: { 
          ...prev.cover, 
          status: 'error', 
          error: error.message 
        }
      }));
      
      // Clear any preview
      if (uploadedFiles.coverPreview) {
        URL.revokeObjectURL(uploadedFiles.coverPreview);
      }
      
      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: '',
        coverDimensions: { width: 0, height: 0, aspectRatio: 0 }
      }));
      
      setImageValidation({
        isValid: false,
        message: '',
        warnings: []
      });
    }
  };

  // Handle PDF upload
  const handlePDFUpload = async (file) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      alert('PDF file must be less than 50MB');
      return;
    }
    
    setUploadProgress(prev => ({
      ...prev,
      pdf: { progress: 0, status: 'uploading', fileName: file.name, error: null }
    }));
    
    try {
      const uploadData = await digitalOceanService.generateBookPDFUploadURL({
        filename: file.name
      });
      
      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to get upload URL');
      }
      
      await digitalOceanService.uploadToSpaces(
        uploadData.upload_url,
        uploadData.fields,
        file
      );
      
      setUploadProgress(prev => ({
        ...prev,
        pdf: { ...prev.pdf, status: 'success', progress: 100 }
      }));
      
      setUploadedFiles(prev => ({
        ...prev,
        pdfUrl: uploadData.public_url
      }));
      
    } catch (error) {
      console.error('PDF upload error:', error);
      setUploadProgress(prev => ({
        ...prev,
        pdf: { 
          ...prev.pdf, 
          status: 'error', 
          error: error.message || 'Upload failed' 
        }
      }));
    }
  };

  const handleFileSelect = (type) => (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (type === 'cover') {
      handleCoverUpload(file);
    } else if (type === 'pdf') {
      handlePDFUpload(file);
    }
  };

  const validateForm = () => {
    const required = ['title', 'description', 'author_bio', 'genre', 'price', 'author_name', 'author_email'];
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      alert(`Please fill in all required fields: ${missing.join(', ')}`);
      return false;
    }

    if (!uploadedFiles.coverUrl) {
      alert('Please upload a cover image');
      return false;
    }

    if (!uploadedFiles.pdfUrl) {
      alert('Please upload a PDF file');
      return false;
    }

    if (parseFloat(formData.price) <= 0) {
      alert('Price must be greater than 0');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const bookData = {
        ...formData,
        genre: parseInt(formData.genre),
        price: parseFloat(formData.price),
        cover_image_url: uploadedFiles.coverUrl,
        full_pdf_url: uploadedFiles.pdfUrl,
        is_admin_upload: true,
        sample_pages_count: parseInt(formData.sample_pages_count),
        // Include image metadata for validation
        cover_image_metadata: {
          width: uploadedFiles.coverDimensions.width,
          height: uploadedFiles.coverDimensions.height,
          aspect_ratio: uploadedFiles.coverDimensions.aspectRatio
        }
      };

      const response = await booksService.adminUploadBook(bookData);
      
      console.log('📘 Book upload response:', response);
      
      if (response.success) {
        alert(`✅ Book uploaded successfully!\n📄 ${response.pages_extracted} pages extracted\n📖 Total pages: ${response.total_pages}`);
        
        if (response.pages_extracted < 5) {
          const retry = window.confirm(
            `⚠️ Only ${response.pages_extracted} pages were extracted.\n` +
            `Would you like to manually trigger page extraction?`
          );
          
          if (retry) {
            await booksService.extractBookPages(response.book.id);
            alert('🔄 Page extraction started in background');
          }
        }
        
        navigate(`/book/${response.book.id}`);
      } else {
        throw new Error(response.error || 'Upload failed');
      }
      
    } catch (error) {
      console.error('❌ Error uploading book:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearCoverImage = () => {
    if (uploadedFiles.coverPreview) {
      URL.revokeObjectURL(uploadedFiles.coverPreview);
    }
    
    setUploadedFiles(prev => ({
      ...prev,
      coverUrl: '',
      coverPreview: '',
      coverDimensions: { width: 0, height: 0, aspectRatio: 0 }
    }));
    
    setUploadProgress(prev => ({
      ...prev,
      cover: { progress: 0, status: 'idle', fileName: '', error: null }
    }));
    
    setImageValidation({
      isValid: false,
      message: '',
      warnings: []
    });
    
    // Reset file input
    const fileInput = document.getElementById('cover-upload');
    if (fileInput) fileInput.value = '';
  };

  const UploadProgressBar = ({ type, progress, fileName, status, error }) => {
    return (
      <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium text-gray-700 truncate">
            {fileName}
          </div>
          <div className="text-sm text-gray-500">
            {status === 'uploading' ? `${progress}%` : status}
          </div>
        </div>
        
        {status === 'uploading' && (
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        
        {status === 'success' && (
          <div className="flex items-center text-green-600 text-sm">
            <CheckCircle className="h-4 w-4 mr-1" />
            Upload complete
          </div>
        )}
        
        {status === 'error' && (
          <div className="flex items-center text-red-600 text-sm">
            <AlertCircle className="h-4 w-4 mr-1" />
            {error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-full bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/books')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Book</h1>
                <p className="text-gray-600 font-medium">
                  Add a new book to the platform as an administrator
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cover Image Specifications Card - Updated */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-300 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertOctagon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-orange-800 font-bold mb-2">
                ⚠️ STRICT COVER IMAGE REQUIREMENTS
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center text-sm text-orange-700 font-medium">
                  <Ruler className="h-4 w-4 mr-2" />
                  <span><strong>Minimum Size:</strong> {MIN_WIDTH}×{MIN_HEIGHT}px</span>
                </div>
                <div className="flex items-center text-sm text-orange-700 font-medium">
                  <Crop className="h-4 w-4 mr-2" />
                  <span><strong>Aspect Ratio:</strong> Close to {MIN_WIDTH}:{MIN_HEIGHT} ({TARGET_RATIO.toFixed(3)})</span>
                </div>
                <div className="flex items-center text-sm text-orange-700">
                  <Upload className="h-4 w-4 mr-2" />
                  <span><strong>Max File Size:</strong> 5MB (JPG, PNG, WebP)</span>
                </div>
                <div className="flex items-center text-sm text-orange-700">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <span><strong>Max Dimensions:</strong> {MIN_WIDTH * MAX_SIZE_MULTIPLIER}×{MIN_HEIGHT * MAX_SIZE_MULTIPLIER}px</span>
                </div>
              </div>
              
              <div className="mt-3 p-2 bg-white border border-orange-200 rounded-lg">
                <p className="text-xs text-orange-800 font-medium mb-1">📏 Recommended Sizes (close to {MIN_WIDTH}:{MIN_HEIGHT} ratio):</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-orange-700">
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    <span>Minimum: {MIN_WIDTH}×{MIN_HEIGHT}px</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    <span>Good: 400×610px</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    <span>Better: 500×762px</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    <span>Best: 600×914px</span>
                  </div>
                </div>
                <p className="text-xs text-orange-600 mt-2">
                  ⚠️ Images that are too large or too far from {MIN_WIDTH}:{MIN_HEIGHT} ratio will be rejected
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Form */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Book Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Book Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Book Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        required
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="Enter book title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description *
                      </label>
                      <textarea
                        name="description"
                        required
                        value={formData.description}
                        onChange={handleChange}
                        rows="4"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none"
                        placeholder="Describe the book..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Author Bio *
                      </label>
                      <textarea
                        name="author_bio"
                        required
                        value={formData.author_bio}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none"
                        placeholder="Tell readers about the author..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Genre *
                        </label>
                        <select
                          name="genre"
                          required
                          value={formData.genre}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        >
                          <option value="">Select Genre</option>
                          {genres.map((genre) => (
                            <option key={genre.id} value={genre.id}>
                              {genre.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Age Rating *
                        </label>
                        <select
                          name="age_rating"
                          required
                          value={formData.age_rating}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        >
                          {ageRatings.map((rating) => (
                            <option key={rating.value} value={rating.value}>
                              {rating.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price (GHS) *
                        </label>
                        <input
                          type="number"
                          name="price"
                          required
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sample Pages
                        </label>
                        <input
                          type="number"
                          name="sample_pages_count"
                          min="1"
                          max="50"
                          value={formData.sample_pages_count}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          First {formData.sample_pages_count} pages as preview
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Author & Files */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Author Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Author Name *
                      </label>
                      <input
                        type="text"
                        name="author_name"
                        required
                        value={formData.author_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="Enter author's name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Author Email *
                      </label>
                      <input
                        type="email"
                        name="author_email"
                        required
                        value={formData.author_email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="Enter author's email"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <ImageIcon className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Book Files (Direct Cloud Upload)
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Cover Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cover Image * <span className="text-orange-600 text-xs">(Close to {MIN_WIDTH}×{MIN_HEIGHT}px)</span>
                      </label>
                      
                      {/* Image Preview with Overlay Info */}
                      {(uploadedFiles.coverPreview || uploadedFiles.coverUrl) && (
                        <div className="mb-4 relative">
                          <div className="relative w-40 h-52 mx-auto">
                            <img
                              ref={imagePreviewRef}
                              src={uploadedFiles.coverPreview || uploadedFiles.coverUrl}
                              alt="Cover preview"
                              className="w-full h-full object-cover rounded-lg shadow-md border border-gray-300"
                              style={{ aspectRatio: `${MIN_WIDTH}/${MIN_HEIGHT}` }}
                            />
                            
                            {/* Image Info Overlay */}
                            {uploadedFiles.coverDimensions.width > 0 && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 rounded-b-lg">
                                <div className="flex justify-between items-center">
                                  <span>
                                    {uploadedFiles.coverDimensions.width}×{uploadedFiles.coverDimensions.height}px
                                  </span>
                                  <span>
                                    Ratio: {uploadedFiles.coverDimensions.aspectRatio.toFixed(3)}
                                  </span>
                                </div>
                                <div className="text-[10px] text-gray-300 mt-1">
                                  Target: {TARGET_RATIO.toFixed(3)} ({MIN_WIDTH}:{MIN_HEIGHT})
                                </div>
                              </div>
                            )}
                            
                            {/* Clear Button */}
                            <button
                              type="button"
                              onClick={clearCoverImage}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          
                          {/* Validation Message */}
                          {imageValidation.message && (
                            <div className={`mt-3 p-3 rounded-lg text-sm ${
                              imageValidation.isValid 
                                ? 'bg-green-50 border border-green-200 text-green-700' 
                                : 'bg-red-50 border border-red-200 text-red-700'
                            }`}>
                              <div className="flex items-center">
                                {imageValidation.isValid ? (
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                ) : (
                                  <XCircle className="h-4 w-4 mr-2 text-red-500" />
                                )}
                                <span className="font-medium">{imageValidation.message}</span>
                              </div>
                              
                              {imageValidation.warnings.length > 0 && (
                                <div className="mt-2 pl-6">
                                  <p className="text-xs font-medium mb-1">💡 Recommendations:</p>
                                  <ul className="text-xs text-orange-600 space-y-1">
                                    {imageValidation.warnings.map((warning, index) => (
                                      <li key={index} className="flex items-start">
                                        <Lightbulb className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                                        {warning}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="cover-upload"
                          accept="image/*"
                          onChange={handleFileSelect('cover')}
                          className="hidden"
                        />
                        <label
                          htmlFor="cover-upload"
                          className="cursor-pointer block"
                        >
                          {uploadedFiles.coverPreview ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-center">
                                <Upload className="h-8 w-8 text-gray-400" />
                              </div>
                              <p className="text-sm text-gray-600">
                                Replace cover image
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <ImageIcon className="h-10 w-10 text-gray-400 mx-auto" />
                              <div>
                                <p className="text-sm font-medium text-gray-700">Upload Cover Image</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Click or drag to upload
                                </p>
                                <p className="text-xs text-orange-600 mt-2 font-medium">
                                  Must be close to {MIN_WIDTH}×{MIN_HEIGHT}px
                                </p>
                              </div>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {uploadProgress.cover.status !== 'idle' && (
                        <UploadProgressBar
                          type="cover"
                          progress={uploadProgress.cover.progress}
                          fileName={uploadProgress.cover.fileName}
                          status={uploadProgress.cover.status}
                          error={uploadProgress.cover.error}
                        />
                      )}
                    </div>

                    {/* PDF Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Book PDF *
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="pdf-upload"
                          accept=".pdf"
                          onChange={handleFileSelect('pdf')}
                          className="hidden"
                        />
                        <label
                          htmlFor="pdf-upload"
                          className="cursor-pointer block"
                        >
                          {uploadedFiles.pdfUrl ? (
                            <div className="space-y-2">
                              <FileText className="h-8 w-8 text-green-500 mx-auto" />
                              <p className="text-sm text-green-600 font-medium">
                                ✓ PDF uploaded to cloud storage
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <FileText className="h-8 w-8 text-gray-400 mx-auto" />
                              <p className="text-sm text-gray-600">
                                Click to upload PDF
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF up to 50MB
                              </p>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {uploadProgress.pdf.status !== 'idle' && (
                        <UploadProgressBar
                          type="pdf"
                          progress={uploadProgress.pdf.progress}
                          fileName={uploadProgress.pdf.fileName}
                          status={uploadProgress.pdf.status}
                          error={uploadProgress.pdf.error}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                  >
                    <option value="approved">Approved (Publish Immediately)</option>
                    <option value="pending">Pending Review</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !uploadedFiles.coverUrl || !uploadedFiles.pdfUrl}
                  className="w-full bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white py-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CloudUpload className="h-4 w-4 mr-2" />
                      Submit Book
                    </>
                  )}
                </button>

                {/* Admin Notes */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900 mb-1">
                        Admin Upload Notes
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• Files upload directly to secure cloud storage (DigitalOcean Spaces)</li>
                        <li>• Cover images must be close to {MIN_WIDTH}×{MIN_HEIGHT}px ({TARGET_RATIO.toFixed(3)} aspect ratio)</li>
                        <li>• Books uploaded as "Approved" will be immediately available to readers</li>
                        <li>• Sample pages will be automatically extracted from PDF</li>
                        <li>• Author will be notified if an account exists with the provided email</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminBookUpload;